import { useContext, useMemo } from "react";
import {
  BlankMediaTypeImporterCountryViewModel,
  ItemViewModel,
  SubmitBlankMediaImporterApplication,
} from "../../../../../../generated/data-contracts";
import { BlankMediaCountriesMode } from "../../../../../../models";
import {
  IAvisEditableListFieldConfig,
  IRowState,
} from "../../../../../shared/components/editableList";
import {
  AvisDropdown,
  AvisInputInt,
} from "../../../../../shared/components/inputs";
import { AvisContext } from "../../../../../shared/context";
import { ApplicationSectionBase, ISectionSharedProps } from "../../forms";
import { fieldCollectionEquipmentAndCNCode } from "./fields/SharedFieldCollection";
import { indexArrayByKey } from "../../../../../../utils";

interface IBlankMediaApplicationCountriesProps
  extends ISectionSharedProps<SubmitBlankMediaImporterApplication> {
  mode: BlankMediaCountriesMode;
  countries: ItemViewModel[] | undefined;
}
export function BlankMediaApplicationCountries(
  props: Readonly<IBlankMediaApplicationCountriesProps>,
) {
  const { bag, bagField, equipments, countries, mode, disabled, loading } =
    props;
  const { res } = useContext(AvisContext);

  const countriesMap = useMemo(
    () => indexArrayByKey(countries || [], "id"),
    [countries],
  );

  const sharedFields = useMemo(
    () =>
      fieldCollectionEquipmentAndCNCode<BlankMediaTypeImporterCountryViewModel>(
        equipments,
        res,
        disabled,
      ),
    [equipments, res, disabled],
  );
  const fieldTitleRegion =
    mode === "Import"
      ? res("blankMedia_Application_label_CountryRegionImported")
      : res("blankMedia_Application_label_CountryRegionExported");

  const fieldCollection: IAvisEditableListFieldConfig<
    IRowState<BlankMediaTypeImporterCountryViewModel>
  >[] = [
    ...sharedFields,
    {
      name: "countryRegionId",
      title: fieldTitleRegion,
      required: true,
      minWidth: 300,
      maxWidth: 300,
      transformViewValue: (fieldValue) => {
        return countriesMap.get(fieldValue)?.name ?? "";
      },
      renderField: (field, prefix, i, val, onChange) => {
        return (
          <AvisDropdown<ItemViewModel>
            name={`${prefix}.${i}.${field.name}`}
            onChange={onChange}
            options={countries}
            optionValue="id"
            optionLabel="name"
            value={val[field.name]}
            hideClear
            required={field.required}
            filter
            disabled={disabled}
          />
        );
      },
    },
    {
      name: "count",
      title: res("blankMedia_Application_label_Count"),
      required: true,
      minWidth: 75,
      width: 75,
      renderField: (field, prefix, i, val, onChange) => {
        return (
          <AvisInputInt
            name={`${prefix}.${i}.${field.name}`}
            value={val[field.name]?.toString()}
            onChange={onChange}
            min={1}
            disabled={disabled}
            required={field.required}
            hideClear
          />
        );
      },
    },
  ];

  return (
    <ApplicationSectionBase
      title={
        mode === "Import"
          ? res("blankMedia_Application_section_I_Imported")
          : res("blankMedia_Application_section_I_Exported")
      }
      textToDisplay={
        mode === "Import"
          ? "portal_TDN_ImporterReport_Import"
          : "portal_TDN_ImporterReport_Export"
      }
      bag={bag}
      bagField={bagField}
      initValue={
        { rowState: "Add" } as IRowState<BlankMediaTypeImporterCountryViewModel>
      }
      fieldCollection={fieldCollection}
      loading={loading}
      disabled={disabled}
      forceUpdateKey={{ loading }}
    />
  );
}
